import React from "react";
import SkeletonLoader from "components/ui/SkeletonLoader";
import './JobDetailViewSkeleton.css';

/**
 * Skeleton that mirrors the JobDetailView layout:
 *  - Hero card  (icon + title + meta + status + action buttons)
 *  - Tab bar    (Overview | Candidates)
 *  - Info strip (4 stat cards)
 *  - Main grid  (left: description + responsibilities/requirements + skills)
 *               (right sidebar: overview rows)
 */
const JobDetailViewSkeleton: React.FC = () => (
  <div className="ue-root">
    <div className="uev-shell">

      {/* ── Hero card ── */}
      <section
        style={{
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 16,
          padding: "20px 24px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 16,
          boxShadow: "0 2px 8px rgba(41,41,41,0.05)",
        }}
      >
        <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
          {/* briefcase icon bubble */}
          <SkeletonLoader width={48} height={48} style={{ borderRadius: 12, flexShrink: 0 }} />
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {/* Job title */}
            <SkeletonLoader width={260} height={24} />
            {/* Meta row: posted · location · work mode */}
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              <SkeletonLoader width={160} height={13} />
              <SkeletonLoader width={140} height={13} />
              <SkeletonLoader width={70} height={13} />
            </div>
            {/* Status badge */}
            <SkeletonLoader width={70} height={24} style={{ borderRadius: 999 }} />
          </div>
        </div>

        {/* Action buttons: Back · Save Draft · Edit */}
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          <SkeletonLoader width={72} height={36} style={{ borderRadius: 8 }} />
          <SkeletonLoader width={90} height={36} style={{ borderRadius: 8 }} />
          <SkeletonLoader width={72} height={36} style={{ borderRadius: 8 }} />
        </div>
      </section>

      {/* ── Tab bar ── */}
      <div
        style={{
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          padding: "0 20px",
          display: "flex",
          gap: 24,
          alignItems: "center",
          height: 48,
        }}
      >
        {[{ w: 80, active: true }, { w: 100, active: false }].map(({ w, active }, i) => (
          <div key={i} style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "center", height: "100%", justifyContent: "center" }}>
            <SkeletonLoader width={w} height={14} />
            {active && <div style={{ height: 2, width: w, background: "#457EFF", borderRadius: 2, opacity: 0.3 }} />}
          </div>
        ))}
      </div>

      {/* ── Info strip: 4 stat cards ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 12,
        }}
      >
        {[
          { label: "OPEN POSITIONS", w: 30 },
          { label: "PACKAGE", w: 140 },
          { label: "INDUSTRY", w: 120 },
          { label: "POSTED", w: 160 },
        ].map(({ label, w }) => (
          <div
            key={label}
            style={{
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: 14,
              padding: "16px 18px",
              display: "flex",
              gap: 12,
              alignItems: "center",
            }}
          >
            <SkeletonLoader width={36} height={36} style={{ borderRadius: 10, flexShrink: 0 }} />
            <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
              <SkeletonLoader width={70} height={11} />
              <SkeletonLoader width={w} height={15} />
            </div>
          </div>
        ))}
      </div>

      {/* ── Main grid: left column + right sidebar ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: 16, alignItems: "start" }}>

        {/* Left column */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

          {/* Job Description card */}
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 24 }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 16 }}>
              <SkeletonLoader width={20} height={20} style={{ borderRadius: 4 }} />
              <SkeletonLoader width={140} height={18} />
            </div>
            <SkeletonLoader count={3} height={14} width="100%" />
            <SkeletonLoader width="60%" height={14} />
          </div>

          {/* Responsibilities + Requirements side by side */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {["Responsibilities", "Requirements"].map((title) => (
              <div key={title} style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 24 }}>
                <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14 }}>
                  <SkeletonLoader width={20} height={20} style={{ borderRadius: 4 }} />
                  <SkeletonLoader width={120} height={16} />
                </div>
                {[1, 2, 3].map((i) => (
                  <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
                    <SkeletonLoader width={8} height={8} circle />
                    <SkeletonLoader width={`${70 + i * 8}%`} height={13} />
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* Required Skills card */}
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 24 }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 16 }}>
              <SkeletonLoader width={20} height={20} style={{ borderRadius: 4 }} />
              <SkeletonLoader width={130} height={16} />
              <SkeletonLoader width={28} height={22} style={{ borderRadius: 999, marginLeft: "auto" }} />
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {[90, 110, 80, 130, 95, 75].map((w, i) => (
                <SkeletonLoader key={i} width={w} height={28} style={{ borderRadius: 999 }} />
              ))}
            </div>
          </div>

          {/* Benefits & Perks card */}
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 24 }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 16 }}>
              <SkeletonLoader width={20} height={20} style={{ borderRadius: 4 }} />
              <SkeletonLoader width={140} height={16} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {[1, 2, 3, 4].map((i) => (
                <div key={i} style={{ display: "flex", gap: 10, alignItems: "center", padding: "10px 12px", background: "#f8faff", borderRadius: 10 }}>
                  <SkeletonLoader width={28} height={28} circle />
                  <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                    <SkeletonLoader width={90} height={13} />
                    <SkeletonLoader width={120} height={11} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right sidebar — Overview rows */}
        <div
          style={{
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 14,
            padding: 24,
          }}
        >
          <SkeletonLoader width={80} height={18} style={{ marginBottom: 18 }} />
          {[
            ["Job Position", 140],
            ["Industry", 110],
            ["Location", 130],
            ["Work Mode", 70],
            ["Experience", 90],
            ["Salary", 120],
            ["Openings", 30],
            ["Qualification", 100],
            ["Gender Pref.", 60],
            ["Emp. Type", 80],
          ].map(([label, w]) => (
            <div
              key={label as string}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "10px 0",
                borderBottom: "1px solid #f4f4f4",
              }}
            >
              <SkeletonLoader width={80} height={13} />
              <SkeletonLoader width={w as number} height={13} />
            </div>
          ))}
        </div>
      </div>

    </div>
  </div>
);

export default JobDetailViewSkeleton;
